/**
 * useBoardLayout Hook
 *
 * Shared layout calculation for Board and LoginBoard.
 * Computes scale and position synchronously on initialization
 * to prevent position jumps on first render.
 */

import { useState, useEffect } from 'react';

export const BOARD_WIDTH = 1000;
export const BOARD_HEIGHT = 1000;
export const HEADER_BAR_HEIGHT = 36;
const HEADER_GAP = 22;

const MIN_BOARD = 300;    // board never smaller than 300px — fits 320px screens with 10px margins
const MAX_BOARD = 1000;   // board never larger than 1000px
const MIN_ACCEPTABLE = 700; // preferred minimum before growing bottom margin
const TARGET_BOTTOM_MARGIN = 200; // ideal bottom margin on desktop
const MIN_MARGIN = 10;    // minimum left/right/bottom margin (always enforced)
const DESKTOP_BREAKPOINT = 768;

// The total group height is (BOARD_HEIGHT + header) * scale.
// Header (buttons + gap) scales with the board.
const HEADER_UNSCALED = HEADER_BAR_HEIGHT + HEADER_GAP;
const TOTAL_UNSCALED = BOARD_HEIGHT + HEADER_UNSCALED; // 1000 + 58 = 1058

// Scale thresholds (applied to total group including header)
const MIN_TOTAL = MIN_BOARD * (TOTAL_UNSCALED / BOARD_WIDTH);   // ~634.8
const MIN_ACCEPT_TOTAL = MIN_ACCEPTABLE * (TOTAL_UNSCALED / BOARD_WIDTH); // ~846.4
const MAX_TOTAL = MAX_BOARD * (TOTAL_UNSCALED / BOARD_WIDTH);   // ~1058

function calculateLayout() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw < DESKTOP_BREAKPOINT;

  // Available space for the total group (header + board)
  const availH = vh - MIN_MARGIN;  // subtract minimum bottom margin
  const availW = vw - MIN_MARGIN * 2;  // subtract minimum left + right margins

  // Width-constrained scale (based on board width, since header scales proportionally)
  const scaleW = Math.min(MAX_BOARD, Math.max(MIN_BOARD, availW)) / BOARD_WIDTH;

  // Height-constrained scale using the priority system.
  // availH is the space for the total group (header + board).
  // The thresholds are in terms of total group height.
  //
  // Phase 1: Group grows from MIN_TOTAL to MIN_ACCEPT_TOTAL (min bottom margin only)
  // Phase 2: Group stays at MIN_ACCEPT_TOTAL, extra grows bottom margin toward TARGET
  // Phase 3: Bottom margin at TARGET, extra grows group to MAX_TOTAL
  // Phase 4: Group at MAX_TOTAL, extra is additional margin
  let groupH: number;
  if (availH <= MIN_ACCEPT_TOTAL) {
    // Phase 1: all space to group (clamped to MIN_TOTAL minimum)
    groupH = Math.max(MIN_TOTAL, availH);
  } else if (availH <= MIN_ACCEPT_TOTAL + TARGET_BOTTOM_MARGIN - MIN_MARGIN) {
    // Phase 2: group at MIN_ACCEPT_TOTAL, extra goes to bottom margin
    groupH = MIN_ACCEPT_TOTAL;
  } else if (availH <= MAX_TOTAL + TARGET_BOTTOM_MARGIN) {
    // Phase 3: bottom margin at TARGET, extra grows group
    groupH = Math.min(MAX_TOTAL, availH - TARGET_BOTTOM_MARGIN);
  } else {
    // Phase 4: group at max
    groupH = MAX_TOTAL;
  }

  // Convert group height to board scale
  const scaleH = groupH / TOTAL_UNSCALED;

  // Final scale: min of width/height constraints, clamped to [0.6, 1.0]
  const scale = Math.max(MIN_BOARD / BOARD_WIDTH, Math.min(MAX_BOARD / BOARD_WIDTH, Math.min(scaleW, scaleH)));

  const scaledWidth = BOARD_WIDTH * scale;
  // Header has a minimum scale of 0.7 for readability on mobile
  const headerScale = Math.max(0.7, scale);
  const scaledHeaderH = HEADER_UNSCALED * headerScale;
  const scaledBoardH = BOARD_HEIGHT * scale;
  const scaledTotalH = scaledHeaderH + scaledBoardH;

  // Horizontal centering with minimum side margins
  const left = Math.max(MIN_MARGIN, (vw - scaledWidth) / 2);

  // Vertical positioning
  const TOP_PAD = 10; // breathing room above header buttons

  if (isMobile) {
    // Mobile: group as high as possible, just below top edge.
    // scaledHeaderH already includes HEADER_GAP for spacing.
    return {
      scale,
      left,
      top: TOP_PAD + scaledHeaderH,
      headerTop: TOP_PAD,
    };
  }

  // Desktop: center the group vertically, respecting bottom margin
  const usedH = scaledTotalH + TOP_PAD;
  const bottomMargin = Math.max(MIN_MARGIN, vh - usedH);
  const groupTop = Math.max(TOP_PAD, (vh - usedH - bottomMargin) / 2 + TOP_PAD);

  return {
    scale,
    left,
    top: groupTop + scaledHeaderH,
    headerTop: groupTop,
  };
}

export function useBoardLayout() {
  const [layout, setLayout] = useState(calculateLayout);

  useEffect(() => {
    function handleResize() {
      setLayout(calculateLayout());
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return layout;
}
